import { AfterViewInit, Component, computed, ElementRef, signal, viewChild } from '@angular/core'
import { ICellEditorAngularComp } from 'ag-grid-angular'
import type { ICellEditorParams } from 'ag-grid-community'
import { Option } from '../shared/models/general'
import { EditableRow } from './entity-metadata'

export type SelectCellEditorParams<T> = Readonly<{
  options: readonly Option<unknown>[]
  // provided by EditableTableComponent
  onSelect?: (row: EditableRow<T>, field: string, value: unknown) => void
}>

@Component({
  selector: 'div[app-select-cell-editor]',
  styles: `
    :host {
      width: 100%;
      height: 100%;
      
      select {
        width: 100%;
        font-size: 14px;
        border: none;
      }
    }
  `,
  template: `
    <select
        #select
        class='form-select'
        (change)="onChange($event)"
    >
      <option value='' [selected]="value()===''"></option>
      @for (o of options(); track o.value) {
        <option [value]="o.value" [selected]="isSelected(o.value)">{{ o.display }}</option>
      }
    </select>
  `,
})
export class SelectCellEditorComponent<T> implements ICellEditorAngularComp, AfterViewInit {
  protected readonly params = signal<SelectCellEditorParams<T>>({ options: [] })
  protected readonly row = signal<EditableRow<T> | undefined>(undefined)
  protected readonly field = signal('')
  protected readonly value = signal<string>('')

  protected readonly options = computed((): readonly Option<unknown>[] =>
    this.params().options,
  )

  private readonly selectRef = viewChild<ElementRef<HTMLSelectElement>>('select')

  ngAfterViewInit() {
    setTimeout(() => {
      const element = this.selectRef()?.nativeElement
      if (element) {
        element.focus()
      }
    }, 0)
  }

  agInit({ value, data, colDef }: ICellEditorParams<EditableRow<T>, string>) {
    this.params.set(colDef.cellEditorParams as SelectCellEditorParams<T>)
    this.row.set(data)
    this.field.set(colDef.field as string)
    this.value.set(String(value ?? ''))
  }

  getValue(): unknown {
    return this.options().find(_ => String(_.value) === this.value())?.value
  }

  protected isSelected(value: unknown) {
    return this.value() == String(value)
  }

  protected onChange({ target }: Event) {
    this.value.set((target as HTMLSelectElement).value)
    const { onSelect } = this.params()
    const row = this.row()
    if (onSelect && row) {
      onSelect(row, this.field(), this.getValue())
    }
  }
}